import React, { useState, useEffect } from "react";
import girlImage from "../img/book.jpeg"; // Replace with your image path
import { FaFacebookF, FaInstagram, FaLinkedinIn } from "react-icons/fa";
import axios from "axios";
import { FaSpinner } from "react-icons/fa6";
import drawingMetricsService from "../services/DrawingMetricsService";

const PromoPopup = ({ isOpen, onClose }) => {

  const [formData, setFormData] = useState({
    email: "",
    firstName: "",
    lastName: "",
    schoolName: ""
  });
  const [successMessage, setSuccessMessage] = useState("");
  const [errorMessage, setErrorMessage] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [validationErrors, setValidationErrors] = useState({});

  // Initialize metrics tracking when popup opens
  useEffect(() => {
    if (isOpen) {
      drawingMetricsService.trackPopupView();
    }
  }, [isOpen]);

  const isValidEmail = (email) => {
    return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);
  };

  const validateForm = () => {
    const errors = {};

    if (!formData.email.trim()) {
      errors.email = "Email is required";
    } else if (!isValidEmail(formData.email)) {
      errors.email = "Please enter a valid email address";
    }

    if (!formData.firstName.trim()) {
      errors.firstName = "First name is required";
    }

    if (!formData.lastName.trim()) {
      errors.lastName = "Last name is required";
    }

    setValidationErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleInputChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    setErrorMessage("");
    setSuccessMessage("");

    // Clear validation error for this field
    if (validationErrors[field]) {
      setValidationErrors(prev => ({ ...prev, [field]: "" }));
    }

    // Track field interaction
    drawingMetricsService.trackFormFieldInteraction(field, 'starts');
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (isLoading) return;

    // Validate form
    if (!validateForm()) {
      return;
    }

    setIsLoading(true);
    const startTime = Date.now();

    try {
      // Track form start
      drawingMetricsService.trackFormStart();

      const response = await axios.post(`${process.env.REACT_APP_API_URL || 'https://api.angelwatchedu.org/api'}/monthly-drawing/enter-monthly-drawing`, {
        email: formData.email.trim(),
        firstName: formData.firstName.trim(),
        lastName: formData.lastName.trim(),
        schoolName: formData.schoolName.trim() || null
      });

      if (response.status === 201) {
        // Track successful completion
        const completionTime = Date.now() - startTime;
        drawingMetricsService.trackFormCompletion(formData, completionTime);
        drawingMetricsService.trackDrawingEntry(formData);

        const successMsg = "🎉 You're entered in our monthly drawing! Check your email for confirmation.";
        console.log("Setting success message:", successMsg);
        setSuccessMessage(successMsg);
        setFormData({
          email: "",
          firstName: "",
          lastName: "",
          schoolName: ""
        });
        setValidationErrors({});
      }
    } catch (err) {
      // Track form abandonment
      drawingMetricsService.trackFormAbandon('submission_error', { error: err.message });

      if (err.response?.status === 400 && err.response?.data?.error?.includes('already entered')) {
        const message = "You've already entered today! Try again tomorrow.";
        console.log("Setting error message:", message);
        setErrorMessage(message);
      } else {
        const message = err.response?.data?.error || "Something went wrong. Please try again.";
        console.log("Setting error message:", message);
        setErrorMessage(message);
      }
    } finally {
      setIsLoading(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/30 px-4">
      <div className="relative bg-gradient-to-br from-yellow-400 to-yellow-500 rounded-xl p-3 md:p-4 flex flex-col md:flex-row items-center gap-3 w-full max-w-md shadow-lg">
        {/* Close Button */}
        <button
          onClick={onClose}
          className="absolute top-4 right-4 text-black hover:text-gray-600 text-3xl font-bold"
        >
          &times;
        </button>

        {/* Left Side: Text and Form */}
        <div className="w-full md:w-1/2">
          <h2 className="text-lg md:text-xl font-bold text-black mb-1 text-center">
            🎉 Monthly Drawing<br />
            <span className="text-black">Win FREE Certification!</span>
          </h2>

          <p className="text-black text-xs mb-2 text-center">
            Protect More Schools With Us<br />
            Subscribe today and be part of our mission. Each month, one subscribed school or daycare will receive a full AngelWatch Certification Program at no cost.
          </p>

          {/* Monthly Drawing Form */}
          <form onSubmit={handleSubmit} className="space-y-1">
            {/* Email Field */}
            <div className="form-group">
              <input
                type="email"
                value={formData.email}
                onChange={(e) => handleInputChange('email', e.target.value)}
                placeholder="Email Address *"
                className="w-full px-2 py-1 bg-white border-none rounded text-xs text-black placeholder-gray-500 shadow-sm"
                required
              />
              {validationErrors.email && (
                <p className="text-red-200 text-xs mt-1">{validationErrors.email}</p>
              )}
            </div>

            {/* First Name Field */}
            <div className="form-group">
              <input
                type="text"
                value={formData.firstName}
                onChange={(e) => handleInputChange('firstName', e.target.value)}
                placeholder="First Name *"
                className="w-full px-2 py-1 bg-white border-none rounded text-xs text-black placeholder-gray-500 shadow-sm"
                required
              />
              {validationErrors.firstName && (
                <p className="text-red-200 text-xs mt-1">{validationErrors.firstName}</p>
              )}
            </div>

            {/* Last Name Field */}
            <div className="form-group">
              <input
                type="text"
                value={formData.lastName}
                onChange={(e) => handleInputChange('lastName', e.target.value)}
                placeholder="Last Name *"
                className="w-full px-2 py-1 bg-white border-none rounded text-xs text-black placeholder-gray-500 shadow-sm"
                required
              />
              {validationErrors.lastName && (
                <p className="text-red-200 text-xs mt-1">{validationErrors.lastName}</p>
              )}
            </div>

            {/* School Name Field (Optional) */}
            <div className="form-group">
              <input
                type="text"
                value={formData.schoolName}
                onChange={(e) => handleInputChange('schoolName', e.target.value)}
                placeholder="School Name (Optional)"
                className="w-full px-2 py-1 bg-white border-none rounded text-xs text-black placeholder-gray-500 shadow-sm"
              />
            </div>

            {/* Submit Button */}
            <button
              type="submit"
              disabled={isLoading}
              className="w-full bg-white text-yellow-500 py-2 px-3 border-none rounded font-bold text-xs cursor-pointer transition-all duration-300 hover:bg-gray-100 disabled:opacity-70 disabled:cursor-not-allowed"
            >
              {isLoading ? <FaSpinner className="animate-spin mx-auto" /> : "Subscribe Now"}
            </button>
          </form>

          {/* Drawing Details */}
          <div className="bg-white bg-opacity-10 p-2 rounded mt-2 text-xs text-black leading-relaxed">
            <p className="mb-1">• Winner selected at the end of each month</p>
            <p className="mb-1">• You can enter once per day</p>
            <p className="mb-1">• No purchase necessary</p>
            <p>• Winner will be notified via email</p>
          </div>

          {/* Success/Error Messages */}
          {successMessage && (
            <div className="bg-green-100 border border-green-300 text-green-800 text-xs mt-1 p-1 rounded text-center font-medium">
              {successMessage}
            </div>
          )}
          {errorMessage && (
            <div className="bg-red-100 border border-red-300 text-red-800 text-xs mt-1 p-1 rounded text-center font-medium">
              {errorMessage}
            </div>
          )}

          {/* Social Media Icons */}
          <div className="flex gap-2 mt-2 text-black text-sm justify-center">
            <a
              href="https://www.facebook.com/AngelWatchAccreditation"
              target="_blank"
              rel="noopener noreferrer"
              className="hover:text-gray-600 transition"
            >
              <FaFacebookF />
            </a>
            <a
              href="https://www.instagram.com/angelwatchusa"
              target="_blank"
              rel="noopener noreferrer"
              className="hover:text-gray-600 transition"
            >
              <FaInstagram />
            </a>
            <a
              href="https://www.linkedin.com/company/angelwatch-national-accreditation-program/?viewAsMember=true"
              target="_blank"
              rel="noopener noreferrer"
              className="hover:text-gray-600 transition"
            >
              <FaLinkedinIn />
            </a>
          </div>

          {/* Note */}
          <p className="text-xs text-black text-opacity-80 mt-2 text-center">
            We promise not to spam you!
          </p>
        </div>

        {/* Right Side: Image */}
        <div className="w-24 h-24 md:w-32 md:h-32 shrink-0">
          <img
            src={girlImage}
            alt="Girl Holding Books"
            className="w-full h-full object-cover rounded-xl"
          />
        </div>
      </div>
    </div>
  );
};

export default PromoPopup;
